import { useState } from 'react'
import {Copy, ChevronDown, ChevronUp, MessageCircleDashed as MessageCircle} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  PROTOCOLOS_OLEOS,
  gerarTextoCompletoWhatsApp,
  instrucaoInternaAgua,
} from '@/utils/protocoloUso'
import { copiarTexto } from '@/utils/clipboard'
import {
  FICHAS_SITUACOES,
  indicadoDaFicha,
  linhasDaSituacao,
} from '@/utils/fichasSituacoes'

interface ProtocoloUsoProps {
  lead: any
}

export default function ProtocoloUso({ lead }: ProtocoloUsoProps) {
  const [aberto, setAberto] = useState(false)

  const oleosIndicados: string[] = lead.oleos_indicados || []
  const nomeCliente = lead.nome || lead.nome_completo || ''

  // Texto completo formatado para WhatsApp (sem o bloco de Protocolo Prioritário)
  const textoCompleto = gerarTextoCompletoWhatsApp(oleosIndicados, nomeCliente)

  const copiarProtocolo = async () => {
    try {
      await copiarTexto(textoCompleto)
      toast.success('Protocolo completo copiado!')
    } catch {
      toast.error('Erro ao copiar')
    }
  }

  // Detectar conflito Limão Siciliano x Mix Metabólico (Meta PWR já contém limão)
  const temMetaPWR = oleosIndicados.includes('Mix Metabólico')
  const temLimao = oleosIndicados.includes('Limão Siciliano')
  const conflitoLimaoMetaPWR = temMetaPWR && temLimao

  // Ordem fixa da manhã: Hortelã → Meta PWR → Limão → Olíbano → Copaíba → On Guard
  const ORDEM_MANHA = ['Hortelã-Pimenta', 'Mix Metabólico', 'Limão Siciliano', 'Olíbano', 'Copaíba', 'Mix Imunidade']
  const ORDEM_DIA = ['Mix para Estabilidade', 'Mix para Aterramento', 'Tangerina', 'Mix Respiratório', 'Mix Metabólico', 'Copaíba', 'Olíbano', 'Limão Siciliano']
  const ORDEM_NOITE = ['Lavanda', 'Tangerina', 'Mix para Estabilidade', 'Mix para Aterramento', 'Copaíba', 'Mix Mensal Feminino', 'Mix Digestivo']

  // Classificação de óleos por período (respeitando ordem e regra de conflito)
  const oleosManha = ORDEM_MANHA.filter(oleo => {
    if (!oleosIndicados.includes(oleo)) return false
    if (conflitoLimaoMetaPWR && oleo === 'Limão Siciliano') return false
    return true
  })

  const oleosDia = ORDEM_DIA.filter(oleo => {
    if (!oleosIndicados.includes(oleo)) return false
    if (oleo === 'Limão Siciliano' && !conflitoLimaoMetaPWR) return false
    return true
  })

  const oleosNoite = ORDEM_NOITE.filter(oleo => oleosIndicados.includes(oleo))

  // Instruções de rotina por óleo
  const instrucoesRotina: Record<string, { manha?: string; dia?: string; noite?: string }> = {
    'Hortelã-Pimenta': { manha: 'inalar 1 gota para energia e foco ao acordar.' },
    'Limão Siciliano': {
      manha: `${instrucaoInternaAgua('após o café da manhã')} Usar garrafa de vidro, nunca plástico.`,
      dia: 'inalar 1 gota para clareza mental e leveza.',
    },
    'Mix Metabólico': {
      manha: `${instrucaoInternaAgua('30 minutos antes do café da manhã')} Usar garrafa de vidro — nunca plástico.`,
      dia: `${instrucaoInternaAgua('30 minutos antes do almoço e no final da tarde')} Usar garrafa de vidro — nunca plástico.`,
    },
    'Mix Imunidade': { manha: instrucaoInternaAgua('após o café da manhã') },
    'Olíbano': {
      manha: '1 gota sublingual após o café da manhã.',
      dia: '1 gota sublingual após o almoço.',
    },
    'Copaíba': {
      manha: '1 gota sublingual após o café da manhã.',
      dia: '1 gota sublingual após o almoço.',
      noite: '1 gota sublingual após o jantar.',
    },
    'Mix para Estabilidade': {
      dia: 'aplicar na nuca ou pulsos sempre que sentir ansiedade ou estresse.',
      noite: 'aplicar na nuca e sola dos pés antes de dormir.',
    },
    'Mix para Aterramento': {
      dia: 'aplicar na nuca ou pulsos sempre que sentir ansiedade ou estresse.',
      noite: 'aplicar na nuca e sola dos pés antes de dormir.',
    },
    'Tangerina': {
      dia: 'inalar 1 gota para leveza e alegria.',
      noite: `${instrucaoInternaAgua('após o jantar para uma noite repousante')} ${instrucaoInternaAgua('2 a 3 vezes ao dia para imunidade')}`,
    },
    'Mix Respiratório': { dia: 'inalar ou aplicar no peito em momentos de tensão.' },
    'Lavanda': { noite: 'inalar 1 gota ou aplicar no peito, nuca e pulsos antes de dormir.' },
    'Mix Mensal Feminino': { noite: 'aplicar no abdômen ou pulsos à noite.' },
    'Mix Digestivo': { noite: instrucaoInternaAgua('após o jantar, se necessário') },
  }

  if (oleosIndicados.length === 0) {
    return null
  }

  return (
    <div className="border-2 border-verde-claro rounded-xl overflow-hidden">
      {/* Cabeçalho colapsável */}
      <button
        onClick={() => setAberto(!aberto)}
        className="w-full flex items-center justify-between px-6 py-4 bg-verde-muito-claro hover:bg-opacity-80 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">📖</span>
          <h3 className="text-lg font-semibold text-verde-escuro font-cormorant">
            Protocolo de Uso
          </h3>
        </div>
        {aberto ? (
          <ChevronUp className="w-5 h-5 text-verde-escuro" />
        ) : (
          <ChevronDown className="w-5 h-5 text-verde-escuro" />
        )}
      </button>

      {aberto && (
        <div className="p-6 space-y-6 bg-white">
          {/* Cards de protocolos dos óleos indicados */}
          <div className="grid gap-4">
            {oleosIndicados.map((oleo, idx) => {
              const protocolo = PROTOCOLOS_OLEOS[oleo]
              if (!protocolo) return null

              return (
                <div
                  key={idx}
                  className="bg-bege-fundo rounded-xl p-5 border border-bege-medio space-y-2"
                >
                  <h4 className="text-base font-bold text-verde-escuro mb-3">
                    {protocolo.nome}
                  </h4>

                  <div className="space-y-1.5 text-sm text-texto-escuro">
                    {FICHAS_SITUACOES[oleo] ? (
                      <>
                        <p>
                          <strong className="text-marrom-suave">Indicado:</strong>{' '}
                          {indicadoDaFicha(FICHAS_SITUACOES[oleo])}
                        </p>

                        {FICHAS_SITUACOES[oleo].comoAplicar && (
                          <p>
                            <strong className="text-marrom-suave">Como aplicar:</strong>{' '}
                            {FICHAS_SITUACOES[oleo].comoAplicar}
                          </p>
                        )}

                        {FICHAS_SITUACOES[oleo].situacoes.map((situacao, i) => {
                          const linhas = linhasDaSituacao(FICHAS_SITUACOES[oleo], situacao)
                          if (linhas.length === 0) return null
                          return (
                            <div key={i} className="pt-1">
                              <p className="font-semibold text-verde-escuro">
                                {situacao.situacao}:
                              </p>
                              {linhas.map((linha, j) => {
                                const achado = linha.match(/^([^:]{1,40}):\s*(.+)$/)
                                return (
                                  <p key={j} className="pl-2">
                                    {achado ? (
                                      <>
                                        <strong className="text-marrom-suave">
                                          {achado[1]}:
                                        </strong>{' '}
                                        {achado[2]}
                                      </>
                                    ) : (
                                      linha
                                    )}
                                  </p>
                                )
                              })}
                            </div>
                          )
                        })}

                        {FICHAS_SITUACOES[oleo].atencao && (
                          <p className="text-red-600 pt-1">
                            <strong>Atenção:</strong> {FICHAS_SITUACOES[oleo].atencao}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                    <p>
                      <strong className="text-marrom-suave">Indicado:</strong> {protocolo.indicado}
                    </p>

                    {protocolo.aromatico && (
                      <p>
                        <strong className="text-marrom-suave">Aromático:</strong>{' '}
                        {protocolo.aromatico}
                      </p>
                    )}

                    {protocolo.topico && (
                      <p>
                        <strong className="text-marrom-suave">Tópico:</strong> {protocolo.topico}
                      </p>
                    )}

                    {protocolo.topicoGordura && (
                      <p>
                        <strong className="text-marrom-suave">Tópico para gordura localizada:</strong>{' '}
                        {protocolo.topicoGordura}
                      </p>
                    )}

                    {protocolo.topicoHormonal && (
                      <p>
                        <strong className="text-marrom-suave">
                          Tópico para equilíbrio hormonal:
                        </strong>{' '}
                        {protocolo.topicoHormonal}
                      </p>
                    )}

                    {protocolo.topicoOndas && (
                      <p>
                        <strong className="text-marrom-suave">Tópico para ondas de calor:</strong>{' '}
                        {protocolo.topicoOndas}
                      </p>
                    )}

                    {protocolo.interno && (
                      <p>
                        <strong className="text-marrom-suave">Interno:</strong> {protocolo.interno}
                      </p>
                    )}

                    {protocolo.usoDireto && (
                      <p>
                        <strong className="text-marrom-suave">Uso direto:</strong>{' '}
                        {protocolo.usoDireto}
                      </p>
                    )}

                    {protocolo.atencao && (
                      <p className="text-red-600">
                        <strong>Atenção:</strong> {protocolo.atencao}
                      </p>
                    )}
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Rotina Diária */}
          <div className="bg-verde-muito-claro rounded-xl p-5 border-2 border-verde-claro">
            <h4 className="text-lg font-bold text-verde-escuro mb-4 font-cormorant">
              ROTINA DIÁRIA COM SEUS ÓLEOS ESSENCIAIS
            </h4>

            <div className="space-y-4">
              {/* Manhã */}
              {oleosManha.length > 0 && (
                <div>
                  <h5 className="text-sm font-semibold text-verde-escuro mb-2">
                    MANHÃ — Energia, foco e proteção
                  </h5>
                  <ul className="space-y-1.5 text-sm text-texto-escuro">
                    {oleosManha.map((oleo, idx) => {
                      const instrucao = instrucoesRotina[oleo]?.manha
                      if (!instrucao) return null
                      const nomeExibicao = PROTOCOLOS_OLEOS[oleo]?.nome || oleo
                      return (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-verde-claro mt-0.5">•</span>
                          <span>
                            <strong>{nomeExibicao}</strong> → {instrucao}
                          </span>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}

              {/* Dia */}
              {oleosDia.length > 0 && (
                <div>
                  <h5 className="text-sm font-semibold text-verde-escuro mb-2">
                    DIA — Equilíbrio emocional e suporte contínuo
                  </h5>
                  <ul className="space-y-1.5 text-sm text-texto-escuro">
                    {oleosDia.map((oleo, idx) => {
                      const instrucao = instrucoesRotina[oleo]?.dia
                      if (!instrucao) return null
                      const nomeExibicao = PROTOCOLOS_OLEOS[oleo]?.nome || oleo
                      return (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-verde-claro mt-0.5">•</span>
                          <span>
                            <strong>{nomeExibicao}</strong> → {instrucao}
                          </span>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}

              {/* Noite */}
              {oleosNoite.length > 0 && (
                <div>
                  <h5 className="text-sm font-semibold text-verde-escuro mb-2">
                    NOITE — Descanso, relaxamento e regeneração
                  </h5>
                  <ul className="space-y-1.5 text-sm text-texto-escuro">
                    {oleosNoite.map((oleo, idx) => {
                      const instrucao = instrucoesRotina[oleo]?.noite
                      if (!instrucao) return null
                      const nomeExibicao = PROTOCOLOS_OLEOS[oleo]?.nome || oleo
                      return (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-verde-claro mt-0.5">•</span>
                          <span>
                            <strong>{nomeExibicao}</strong> → {instrucao}
                          </span>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}

              {/* Observação */}
              <p className="text-xs text-texto-suave italic mt-4 pt-4 border-t border-bege-medio">
                <strong>Observação:</strong> cada pessoa é única. Respeite como seu corpo reage a
                cada óleo e ajuste conforme sua necessidade. Na dúvida, entre em contato para
                orientação personalizada.
              </p>
            </div>
          </div>

          {/* Botões Copiar e WhatsApp */}
          <div className="flex gap-3">
            <button
              onClick={copiarProtocolo}
              className="flex-1 flex items-center justify-center gap-2 bg-verde-escuro text-white px-4 py-3 rounded-xl hover:bg-opacity-90 transition-all font-medium text-sm"
            >
              <Copy className="w-4 h-4" />
              Copiar protocolo completo
            </button>
            <button
              onClick={() => {
                const numero = (lead.whatsapp || '').replace(/\D/g, '')
                const numeroFormatado = !numero ? '' : numero.startsWith('55') && numero.length >= 12 ? numero : numero.length >= 10 && numero.length <= 11 ? `55${numero}` : numero
                if (!numeroFormatado) {
                  toast.error('WhatsApp da cliente não disponível')
                  return
                }
                const texto = textoCompleto
                const url = `https://wa.me/${numeroFormatado}?text=${encodeURIComponent(texto)}`
                window.open(url, '_blank')
              }}
              className="flex-1 flex items-center justify-center gap-2 text-white px-4 py-3 rounded-xl transition-all font-medium text-sm"
              style={{ backgroundColor: '#25D366' }}
            >
              <MessageCircle className="w-4 h-4" />
              Abrir no WhatsApp
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
